import { buildUrlSearchParamsFromRouteQuery, checkIfFile } from '@/lib/urls.js';
import { commonValues } from '~/config/common.js';

export default defineNuxtRouteMiddleware((to) => {
  const nuxtApp = useNuxtApp();
  const req = useRequestURL();
  const runtimeConfig = useRuntimeConfig();
  const envData = runtimeConfig?.public;
  let actualPath = to?.path;
  const actualQueryParams = buildUrlSearchParamsFromRouteQuery(to?.query);
  let actualQueryParamsString = '';

  const { locale } = nuxtApp.$i18n;

  const oldRwdQueryParams = ['HostelNumber', 'hostelnumber', 'hostelNumber'];

  let shouldRedirect = false;
  let isUserForbiddenToAccess = false;

  // don't touch error routes
  if (actualPath === '/__nuxt_error') {
    return;
  }

  // @TODO: This impacts korean characters, so we are removing for now
  // Ex: /ko/hoseuterdeur/p/3262/banana-벙garro-maui-hoseuter/
  // redirects from uppercase to lowercase ex: /Products -> /products
  /*
  if (actualPath?.match(/[A-Z]/)) {
    actualPath = actualPath?.toLowerCase();
    shouldRedirect = true;
  }
  */

  // don't redirect files and add trailing slash
  if (!checkIfFile(actualPath) && !actualPath?.endsWith('/')) {
    actualPath += '/';
    shouldRedirect = true;
  }

  // Remove '/st/' (ie: old static-pwa url structure) from the path if it exists
  if (actualPath.match(/^\/st\//)) {
    actualPath = actualPath.replace(/^\/st/, '');
    shouldRedirect = true;
  }

  // Check if the path is an old static-pwa redirect path
  if (actualPath.match(/^\/redirect\//) && actualQueryParams.get('path')) {
    actualPath = actualPath.replace(/^\/redirect\//, '/');
    const pathFromQuery = actualQueryParams.get('path');
    actualPath = `${actualPath}${pathFromQuery}`;
    actualQueryParams.delete('path');
    shouldRedirect = true;
  }

  // Redirects users if they are trying to access a page with old RWD query params (hostelnumber)
  // Runs before the language subdomain block so subdomain requests get a single absolute redirect
  const foundOldRwdQueryParam = oldRwdQueryParams.find((oldRwdQueryParam) => oldRwdQueryParam in to?.query);
  if (foundOldRwdQueryParam) {
    const localePrefix = locale?.value !== 'en' ? `/${locale?.value}` : '';
    const propertyId = Number.parseInt(to?.query?.[foundOldRwdQueryParam]);
    if (!Number.isNaN(propertyId)) {
      actualPath = `${localePrefix}/-/p/${propertyId}/-/`;
      actualQueryParams.delete(foundOldRwdQueryParam);
      shouldRedirect = true;
    }
  }

  // If we are on the subdomain language then we should redirect the user for the proper language subfolder
  // @devNote: COD-777 — LANGUAGEMAPPING keys are the whitelist (string check guards inherited Object
  // properties like `constructor`) and the target host comes from runtime config, so the Host header
  // never reaches the redirect target (prevents Open Redirect via spoofed Host)
  const languageSubDomain = req.hostname?.match(/^www\.(\w+)\./i)?.[1]?.toLowerCase();
  const languageFolderCode = commonValues.LANGUAGEMAPPING[languageSubDomain];
  if (typeof languageFolderCode === 'string') {
    if (!actualPath.match(/^\/\w{2}\//)) {
      actualPath = `/${languageFolderCode}${actualPath}`;
    }
    actualPath = `${envData.BASE_URL_PROTOCOL}${envData.BASE_URL_DIVIDER}${envData.DEFAULT_BASE_URL}${actualPath}`;
    shouldRedirect = true;
  }

  // Access to the debug page is forbidden for public users
  // Checked on the original route path (with optional locale prefix) so path mutations can't mask it
  if (to?.path?.match(/^(\/\w{2})?\/_debug(\/|$)/i)) {
    isUserForbiddenToAccess = true;
  }

  // Grant access to forbidden pages only on dev/test environments or if using the dev=1 query param
  if (
    isUserForbiddenToAccess === true
    && (to?.query?.dev === '1' || ['dev', 'test'].includes(envData?.RELEASE_ENV))
  ) {
    console.warn('### Users won\'t be able to access this page in production');
    isUserForbiddenToAccess = false;
  }

  // Redirects the users if the original path has suffered changes and if the user is not forbidden to access the page
  if (shouldRedirect && !isUserForbiddenToAccess && actualPath) {
    actualQueryParamsString
      = actualQueryParams && actualQueryParams?.size > 0 ? `?${actualQueryParams.toString()}` : '';
    console.error(
      `### Redirected from ${req.origin}${to?.path}${actualQueryParamsString} to ${actualPath}${actualQueryParamsString}`,
    );

    return nuxtApp.runWithContext(() =>
      navigateTo(`${actualPath}${actualQueryParamsString}`, {
        redirectCode: 301,
        external: actualPath.match(/^http/),
      }),
    );
  }

  // If user is forbidden to access the page then redirect it to the homepage
  if (isUserForbiddenToAccess && actualPath !== '/') {
    return navigateTo('/');
  }
});
